// THE DEMO YOU SCAN ON YOUR PHONE TELLS THE LAPTOP IT MOVED (TW-0136, 2026-09-15).
//
// Chris, 2026-09-08, on the demo's QR code: "…if someone scans a session QR code from the demo mode the desktop view
// can switch into 'you're completing this on your phone', maybe."
//
// THE WHOLE MECHANISM, and it is deliberately the smallest one that is real. No socket, no session store, nothing kept
// in the browser:
//
//   1. The LAPTOP mints a random id when it shows the QR code, and the code's URL carries it as `?pair=<id>`.
//   2. The PHONE that opens that URL tells the API so, once (`announcePair`), and takes the id off its address bar.
//   3. The laptop asks the API every POLL_EVERY_MS whether a phone has (`pollForPhone`), for POLL_FOR_MS after it last
//      showed the code, and stops at the first yes. Then it paints "You're completing this on your phone" (App.tsx).
//
// The API's half is services/waiver-api/consolidated_api/demo_pair.py, and its header says what is kept: one row per
// pairing, the id and two timestamps, meaningless after 10 minutes.
//
// ⚠ ONLY THE DEMO. Every caller sits behind isDemoShop (subdomain.ts). A real shop's form never reads `?pair=`, never
// announces and never asks: a printed code on a counter opens a consent form and nothing else. App.test.tsx holds that
// half, DemoChrome.test.tsx the demo's.
//
// ⚠ AND NO POLL NOBODY ASKED FOR. A laptop that never opens the QR code never asks once, and one that did stops after 5
// minutes, paired or not, so a demo tab left open on a desk overnight costs nothing. That is also why the route is not
// on the keep-warm walk: the day's first ask pays a cold start of about a second, inside a 4-second wait nobody is
// watching, and a Lambda kept warm all day for a question asked a few times a week is the waste the card ruled out.

import { API_URL } from "./config";

/** How often the laptop asks. Slow on purpose: a phone takes several seconds to scan, open and load the page, so
 *  asking faster buys nothing a person would notice. */
export const POLL_EVERY_MS = 4000;

/** And for how long after the code was last shown. A person scans a code right then or not at all. */
export const POLL_FOR_MS = 5 * 60 * 1000;

let timing = { every: POLL_EVERY_MS, for: POLL_FOR_MS };

/** Tests only: shorter waits, so a whole pairing fits in a test's patience. `null` restores the real ones. */
export function setPairingTiming(next: { every: number; for: number } | null): void {
  timing = next ?? { every: POLL_EVERY_MS, for: POLL_FOR_MS };
}

/** 16 random bytes as 32 lowercase hex characters, the only shape the API accepts (demo_pair.py ID_RE). */
const PAIR_ID = /^[0-9a-f]{32}$/;

export const isPairId = (value: unknown): value is string => typeof value === "string" && PAIR_ID.test(value);

/** A fresh id, or null on a browser with no random source. Then the demo is exactly what it was before this file:
 *  the QR code is the page's own URL, and the laptop never asks. */
export function mintPairId(): string | null {
  try {
    const c = typeof crypto !== "undefined" ? crypto : undefined;
    if (!c || typeof c.getRandomValues !== "function") return null;
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

/** The URL the QR code draws: the page as it is, dials and all, plus the pairing. */
export function withPairId(href: string, id: string): string {
  const url = new URL(href);
  url.searchParams.set("pair", id);
  return url.toString();
}

/** The pairing a phone was handed, or null. Only a well-formed id counts: anything else in `?pair=` is somebody's
 *  typing, and it is not announced. */
export function pairIdFrom(search: string): string | null {
  const value = new URLSearchParams(search).get("pair");
  return isPairId(value) ? value : null;
}

/** The same address without the pairing: what the phone's address bar says once it has announced, so a reload, a
 *  share or a bookmark carries the demo and not somebody's laptop. */
export function withoutPairId(href: string): string {
  const url = new URL(href);
  url.searchParams.delete("pair");
  return url.toString();
}

/** The phone's one announcement. Fire and forget: never throws, never awaited.
 *
 *  A plain string body goes as text/plain, which keeps this a "simple" request with no CORS preflight. The API is a
 *  different origin from demo.waivers.tattoo, and a preflight would add a second round trip to the one moment this
 *  whole file is for. demo_pair.py reads the body as JSON whatever it was called. */
export function announcePair(id: string): void {
  try {
    void fetch(`${API_URL}/demo-pair`, { method: "POST", body: JSON.stringify({ id }) }).catch(() => {});
  } catch {
    /* the demo works the same without it */
  }
}

/** The laptop's one question. Anything but a clean yes is a no, a broken API included, which only ever means the
 *  laptop does not switch. */
export async function askPaired(id: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_URL}/demo-pair?id=${id}`, { cache: "no-store" });
    if (res.status !== 200) return false;
    const body = await res.json();
    return body?.paired === true;
  } catch {
    return false;
  }
}

export type PairingPoll = { stop: () => void };

/** What the poll does at its two ends. `ask` is the API unless a test hands in its own. */
export type PairingHandlers = {
  /** a phone has opened the pairing: called once, at the yes */
  onPaired: () => void;
  /** the deadline came first: called once, so the code on screen goes away with the listening. A code still showing
   *  after the laptop has stopped asking can no longer switch anything, and nothing on the screen would say so. */
  onExpired?: () => void;
  ask?: (id: string) => Promise<boolean>;
};

/** Ask until a phone has opened this pairing. It stops for good at the first yes (`onPaired`), at the deadline
 *  (`onExpired`), or on `stop()` (neither), whichever comes first. One ask at a time: the next is scheduled only after
 *  the last has answered, so a slow API never stacks them up. */
export function pollForPhone(id: string, { onPaired, onExpired, ask = askPaired }: PairingHandlers): PairingPoll {
  const { every, for: span } = timing;
  const deadline = Date.now() + span;
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const tick = async () => {
    timer = null;
    if (stopped) return;
    if (Date.now() >= deadline) {
      stopped = true;
      onExpired?.();
      return;
    }
    let yes = false;
    try {
      yes = await ask(id);
    } catch {
      yes = false;
    }
    if (stopped) return;
    if (yes) {
      stopped = true;
      onPaired();
      return;
    }
    timer = setTimeout(tick, every);
  };

  // not at once: the code was drawn a moment ago, and no phone has scanned it yet
  timer = setTimeout(tick, every);

  return {
    stop: () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
}
